import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { InterceptorUtil, LoggerService, tryNormalizeHttpError, User, UserSignUp } from '@spartacus/core';
import { USER_PROFILE_NORMALIZER, USER_SIGN_UP_SERIALIZER } from '@spartacus/user/profile/core';
import { OccUserProfileAdapter } from '@spartacus/user/profile/occ';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomOcc } from '../../../../../../spartacus/custom/core/occ/occ-models/custom-occ.models';
import { DocumentIdentifierType, JobType, UserTaxGroup } from '../../../../../spartacus-custom.module';
import {
  DOCUMENT_IDENTIFIER_TYPE_NORMALIZER,
  JOB_TYPE_IDENTIFIER_TYPE_NORMALIZER,
  USER_TAX_GROUP_IDENTIFIER_TYPE_NORMALIZER
} from '../../custom-register/store/conectors/converter';
import { CustomUserProfileAdapter } from '../../custom-register/store/conectors/custom-user-profile.adapter';

const CONTENT_TYPE_JSON_HEADER = { 'Content-Type': 'application/json' };
export const CUSTOM_CLIENT_TOKEN = 'custom-use-client-token';

@Injectable()
export class CustomOccUserProfileAdapter extends OccUserProfileAdapter implements CustomUserProfileAdapter {
  protected logger = inject(LoggerService);
  loadJobTypes(): Observable<JobType[]> {
    const url = this.occEndpoints.buildUrl('jobtypes');
    return this.http.get<CustomOcc.JobTypeList>(url).pipe(
      catchError((error) => throwError(tryNormalizeHttpError(error, this.logger))),
      map((JobTypeList) => JobTypeList.jobTypes ?? []),
      this.converter.pipeableMany(JOB_TYPE_IDENTIFIER_TYPE_NORMALIZER)
    );
  }

  loadUserTaxGroups(): Observable<UserTaxGroup[]> {
    const url = this.occEndpoints.buildUrl('usertaxgroups');
    return this.http.get<CustomOcc.UserTaxGroupList>(url).pipe(
      catchError((error) => throwError(tryNormalizeHttpError(error, this.logger))),
      map((UserTaxGroupList) => UserTaxGroupList.enumerations ?? []),
      this.converter.pipeableMany(USER_TAX_GROUP_IDENTIFIER_TYPE_NORMALIZER)
    );
  }

  loadDocumentIdentifierTypes(): Observable<DocumentIdentifierType[]> {
    const url = this.occEndpoints.buildUrl('documentidentifiertypes');
    return this.http.get<CustomOcc.DocumentIdentifierTypeList>(url).pipe(
      catchError((error) => throwError(tryNormalizeHttpError(error, this.logger))),
      map((documentIdentifierTypeList) => documentIdentifierTypeList.documentIdentifierTypes ?? []),
      this.converter.pipeableMany(DOCUMENT_IDENTIFIER_TYPE_NORMALIZER)
    );
  }

  override register(user: UserSignUp): Observable<User> {
    const url: string = this.occEndpoints.buildUrl('userRegister');
    let headers = new HttpHeaders({
      ...CONTENT_TYPE_JSON_HEADER
    });
    headers = InterceptorUtil.createHeader(CUSTOM_CLIENT_TOKEN, true, headers);
    headers = this.appendCaptchaToken(headers);
    user = this.converter.convert(user, USER_SIGN_UP_SERIALIZER);

    return this.http.post<User>(url, user, { headers }).pipe(
      catchError((error) => {
        throw tryNormalizeHttpError(error, this.logger);
      }),
      this.converter.pipeable(USER_PROFILE_NORMALIZER)
    );
  }

  override requestForgotPasswordEmail(userEmailAddress: string): Observable<unknown> {
    const url = this.occEndpoints.buildUrl('userRestoreToken');
    const body = { loginId: userEmailAddress };
    let headers = new HttpHeaders(CONTENT_TYPE_JSON_HEADER);
    headers = InterceptorUtil.createHeader(CUSTOM_CLIENT_TOKEN, true, headers);
    return this.http.post(url, body, { headers }).pipe(
      catchError((error) => {
        throw tryNormalizeHttpError(error, this.logger);
      })
    );
  }
}
